import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { UserData, UserPermissions, UserType } from 'src/lib/fliko/models/users/user.data';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { DatabaseService } from 'src/app/shared/services/database.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(private dbSrv: DatabaseService) {}

  // #region User
  /**
   * @path /user/
   * @key userId
   */
  private get user() {
    return this.dbSrv.commonFactory.getCollection<UserData>('user');
  }

  public getUsers$(): Observable<UserData[]> {
    return this.user.getList();
  }

  public getUsersMap$(): Observable<Map<string, UserData>> {
    return this.user.getMap();
  }

  private userById = (id: string) => this.dbSrv.commonFactory.getObject<UserData>(`user/${id}`);

  public getUser$(id: string): Observable<UserData | Nullish> {
    return this.userById(id).get();
  }

  /**
   * @param {UserType} type
   * @path /user/
   * @key userId
   */
  private userByType = (type: UserType) =>
    this.dbSrv.commonFactory.getCollectionReadOnly<UserData>('user', { by: { child: 'userType' }, equalTo: type });

  // TODO: find uses
  public getUsersByType$(type: UserType): Observable<UserData[]> {
    return this.userByType(type).getList();
  }

  public setUserType(userId: string, type: UserType): Promise<void> {
    return this.user.setElementChild(userId, 'userType', type);
  }

  public setUsersActivity(userId: string): Promise<void> {
    return this.user.setElementChild(userId, 'lastActivity', Date.now());
  }

  public setPasswordDate(userId: string): Promise<void> {
    return this.user.setElementChild(userId, 'passwordSetDate', TimeHlp.todayAt(0));
  }

  public setUserLanguage(userId: string, lang: string): Promise<void> {
    return this.user.setElementChild(userId, 'language', lang);
  }
  // #endregion

  // #region Permissions
  /**
   * @path /userPermissions/
   * @key id
   * @indexOn estateId
   */
  private userPermissionsByEstate = (eId: string) =>
    this.dbSrv.commonFactory.getCollectionReadOnly<UserPermissions>('userPermissions', {
      by: { child: 'estateId' },
      equalTo: eId
    });

  public getPermissionsByEstate$(estateId: string): Observable<UserPermissions[]> {
    if (!estateId) return of([]);
    return this.userPermissionsByEstate(estateId).getList();
  }

  /**
   * @path /userPermissions/
   * @key id
   * @warning USED ONLY FOR SET AND UPDATE
   */
  private get userPermissions() {
    return this.dbSrv.commonFactory.getCollection<UserPermissions>('userPermissions');
  }

  public setUserPermissions(permissions: UserPermissions) {
    return this.userPermissions.setElement(permissions.id, permissions);
  }

  public setUserConversationByDefault(permissionId: string, twoSided: boolean) {
    return this.userPermissions.updateElement(permissionId, { messageResponseDefault: twoSided });
  }

  // #endregion
}
